import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, CheckCircle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

export default function CustomerOrders() {
  const [orders, setOrders] = useState([]);
  const [proofOrderId, setProofOrderId] = useState(null);
  const [proofForm, setProofForm] = useState({ bank_name: '', account_name: '', amount: '', transfer_date: '', proof_url: '', notes: '' });
  const [proofSubmitting, setProofSubmitting] = useState(false);
  const navigate = useNavigate();
  const load = () => api.get('/customer/orders').then(r => setOrders(r.data.orders)).catch(() => {});
  useEffect(() => { load(); }, []);
  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  const statusLabel = (s) => {
    const map = { pending: 'Belum Bayar', paid: 'Sudah Bayar', completed: 'Selesai', pending_confirmation: 'Menunggu Konfirmasi', awaiting_payment: 'Belum Bayar', cancelled: 'Dibatalkan' };
    return map[s] || s;
  };
  const statusColor = (s) => {
    if (s === 'paid' || s === 'completed') return 'bg-emerald-100 text-emerald-700';
    if (s === 'pending_confirmation') return 'bg-blue-100 text-blue-700';
    if (s === 'cancelled') return 'bg-red-100 text-red-700';
    return 'bg-amber-100 text-amber-700';
  };

  const canUploadProof = (o) => {
    return o.payment_provider === 'bank_transfer' && o.status !== 'paid' && o.status !== 'completed' && o.payment_status !== 'paid';
  };

  const handleSubmitProof = async (orderId) => {
    if (!proofForm.bank_name || !proofForm.account_name || !proofForm.amount) { toast.error('Lengkapi field wajib'); return; }
    setProofSubmitting(true);
    try {
      await api.post(`/customer/orders/${orderId}/payment-proof`, {
        bank_name: proofForm.bank_name, account_name: proofForm.account_name,
        amount: parseInt(proofForm.amount), transfer_date: proofForm.transfer_date,
        proof_url: proofForm.proof_url, notes: proofForm.notes
      });
      toast.success('Bukti pembayaran terkirim!');
      setProofOrderId(null);
      setProofForm({ bank_name: '', account_name: '', amount: '', transfer_date: '', proof_url: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal mengirim'); }
    finally { setProofSubmitting(false); }
  };

  return (
    <div data-testid="customer-orders-page">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>Pesanan Saya</h1>
      <Card className="border-slate-200 shadow-sm"><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100">
            <th className="text-left py-3 px-4 text-slate-500 font-medium">Order</th>
            <th className="text-left py-3 px-4 text-slate-500 font-medium">Items</th>
            <th className="text-left py-3 px-4 text-slate-500 font-medium">Total</th>
            <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
            <th className="text-left py-3 px-4 text-slate-500 font-medium">Tanggal</th>
            <th className="text-left py-3 px-4 text-slate-500 font-medium">Aksi</th>
          </tr></thead>
          <tbody>
            {orders.map(o => (
              <React.Fragment key={o.id}>
                <tr className="border-b border-slate-50">
                  <td className="py-3 px-4 font-mono text-xs">{o.order_number}</td>
                  <td className="py-3 px-4">{(o.items || []).map(i => i.product_name).join(', ')}</td>
                  <td className="py-3 px-4 font-medium">Rp {fmt(o.total)}</td>
                  <td className="py-3 px-4">
                    <Badge variant="outline" className={statusColor(o.payment_status || o.status)}>{statusLabel(o.payment_status || o.status)}</Badge>
                  </td>
                  <td className="py-3 px-4 text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString('id-ID')}</td>
                  <td className="py-3 px-4 space-x-2">
                    <button onClick={() => navigate(`/my/orders/${o.id}/tracking`)} className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium">Lacak</button>
                    {canUploadProof(o) && o.payment_status !== 'pending_confirmation' && (
                      <button onClick={() => { setProofOrderId(proofOrderId === o.id ? null : o.id); setProofForm({ bank_name: '', account_name: '', amount: String(o.total || ''), transfer_date: '', proof_url: '', notes: '' }); }}
                        className="text-xs text-emerald-600 hover:text-emerald-800 hover:underline font-medium">Upload Bukti</button>
                    )}
                    {o.payment_status === 'pending_confirmation' && (
                      <span className="text-xs text-blue-500 font-medium">&#10003; Bukti terkirim</span>
                    )}
                  </td>
                </tr>
                {proofOrderId === o.id && (
                  <tr><td colSpan={6} className="p-4 bg-slate-50">
                    <div className="max-w-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-slate-700">Konfirmasi Pembayaran - #{o.order_number}</p>
                        <button onClick={() => setProofOrderId(null)}><X size={16} className="text-slate-400" /></button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">Bank Pengirim *</Label><Input placeholder="BCA, BNI..." value={proofForm.bank_name} onChange={e => setProofForm({...proofForm, bank_name: e.target.value})} /></div>
                        <div><Label className="text-xs">Nama Pengirim *</Label><Input placeholder="Nama di rekening" value={proofForm.account_name} onChange={e => setProofForm({...proofForm, account_name: e.target.value})} /></div>
                        <div><Label className="text-xs">Jumlah Transfer *</Label><Input type="number" value={proofForm.amount} onChange={e => setProofForm({...proofForm, amount: e.target.value})} /></div>
                        <div><Label className="text-xs">Tanggal Transfer</Label><Input type="date" value={proofForm.transfer_date} onChange={e => setProofForm({...proofForm, transfer_date: e.target.value})} /></div>
                      </div>
                      <div><Label className="text-xs">URL Bukti Transfer (opsional)</Label><Input placeholder="https://..." value={proofForm.proof_url} onChange={e => setProofForm({...proofForm, proof_url: e.target.value})} /></div>
                      <Button size="sm" disabled={proofSubmitting} onClick={() => handleSubmitProof(o.id)}>
                        {proofSubmitting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />} Kirim Bukti
                      </Button>
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {!orders.length && <tr><td colSpan={6} className="py-12 text-center text-slate-400">Belum ada pesanan</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
